'use client';

import { useState, useEffect } from 'react';
import { Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis } from 'recharts';
import Icon from '@/components/ui/AppIcon';

interface AdjustmentRecord {
  id: string;
  timestamp: string;
  model: string;
  trigger: string;
  delta: number;
  accuracy_before: number;
  accuracy_after: number;
  type: 'weight' | 'variable' | 'drift' | 'recalibration';
}

interface ModelMetrics {
  name: string;
  learningScore: number;
  adaptationVelocity: number;
  confidenceDecay: number;
  stabilityIndex: number;
  accuracy: number;
  drift: number;
}

const MODELS: ModelMetrics[] = [
  { name: 'Poisson v3', learningScore: 87.4, adaptationVelocity: 2.3, confidenceDecay: 0.12, stabilityIndex: 91.2, accuracy: 68.4, drift: 1.2 },
  { name: 'Elo Advanced', learningScore: 82.1, adaptationVelocity: 1.8, confidenceDecay: 0.09, stabilityIndex: 88.7, accuracy: 71.2, drift: 0.8 },
  { name: 'xG Neural', learningScore: 91.6, adaptationVelocity: 3.1, confidenceDecay: 0.18, stabilityIndex: 84.3, accuracy: 73.8, drift: 2.1 },
  { name: 'Momentum ML', learningScore: 78.9, adaptationVelocity: 1.5, confidenceDecay: 0.07, stabilityIndex: 93.1, accuracy: 65.9, drift: 0.5 },
  { name: 'Hybrid Ensemble', learningScore: 94.2, adaptationVelocity: 2.7, confidenceDecay: 0.14, stabilityIndex: 89.6, accuracy: 76.3, drift: 1.4 },
];

const LEARNING_HISTORY = Array.from({ length: 30 }, (_, i) => ({
  day: `J-${30 - i}`,
  accuracy: 62 + Math.sin(i * 0.3) * 4 + i * 0.4 + Math.random() * 2,
  learningScore: 70 + Math.cos(i * 0.2) * 5 + i * 0.6,
  stability: 80 + Math.sin(i * 0.15) * 3 + i * 0.3,
}));

const ADJUSTMENTS: AdjustmentRecord[] = [
  { id: 'adj-001', timestamp: '2026-05-16 22:41', model: 'xG Neural', trigger: 'Prediction error > 2σ', delta: +3.2, accuracy_before: 70.6, accuracy_after: 73.8, type: 'recalibration' },
  { id: 'adj-002', timestamp: '2026-05-16 20:15', model: 'Poisson v3', trigger: 'Model drift detected', delta: +1.8, accuracy_before: 66.6, accuracy_after: 68.4, type: 'drift' },
  { id: 'adj-003', timestamp: '2026-05-16 18:30', model: 'Hybrid Ensemble', trigger: 'Weight optimization cycle', delta: +2.1, accuracy_before: 74.2, accuracy_after: 76.3, type: 'weight' },
  { id: 'adj-004', timestamp: '2026-05-15 23:10', model: 'Elo Advanced', trigger: 'Variable importance shift', delta: +0.9, accuracy_before: 70.3, accuracy_after: 71.2, type: 'variable' },
  { id: 'adj-005', timestamp: '2026-05-15 19:45', model: 'Momentum ML', trigger: 'Seasonal pattern update', delta: -0.4, accuracy_before: 66.3, accuracy_after: 65.9, type: 'variable' },
  { id: 'adj-006', timestamp: '2026-05-15 14:20', model: 'xG Neural', trigger: 'Goalie data recalibration', delta: +1.5, accuracy_before: 69.1, accuracy_after: 70.6, type: 'recalibration' },
];

const ERROR_ANALYSIS = Array.from({ length: 14 }, (_, i) => ({
  day: `J-${14 - i}`,
  overPrediction: Math.random() * 8 + 2,
  underPrediction: Math.random() * 6 + 1,
  calibrationError: Math.random() * 4 + 0.5,
}));

const TYPE_COLORS: Record<string, string> = {
  weight: 'text-blue-400',
  variable: 'text-yellow-400',
  drift: 'text-orange-400',
  recalibration: 'text-green-400',
};

const TYPE_BG: Record<string, string> = {
  weight: 'bg-blue-400/10 border-blue-400/30',
  variable: 'bg-yellow-400/10 border-yellow-400/30',
  drift: 'bg-orange-400/10 border-orange-400/30',
  recalibration: 'bg-green-400/10 border-green-400/30',
};

export default function AdaptiveLearningClient() {
  const [selectedModel, setSelectedModel] = useState(MODELS[4]);
  const [activeTab, setActiveTab] = useState<'overview' | 'errors' | 'history'>('overview');
  const [liveScore, setLiveScore] = useState(94.2);
  const [cycleCount, setCycleCount] = useState(1847);

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveScore(prev => +(prev + (Math.random() - 0.5) * 0.3).toFixed(1));
      setCycleCount(prev => prev + Math.floor(Math.random() * 3));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const radarData = [
    { metric: 'Précision', value: selectedModel.accuracy },
    { metric: 'Stabilité', value: selectedModel.stabilityIndex },
    { metric: 'Learning', value: selectedModel.learningScore },
    { metric: 'Vélocité', value: selectedModel.adaptationVelocity * 20 },
    { metric: 'Confiance', value: (1 - selectedModel.confidenceDecay) * 100 },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Icon name="CpuChipIcon" size={20} className="text-primary" />
            <h1 className="text-xl font-bold text-foreground">Adaptive Learning Engine</h1>
            <span className="px-2 py-0.5 text-2xs font-mono bg-primary/10 text-primary border border-primary/20 rounded">PHASE QUANT · ÉTAPE 1</span>
          </div>
          <p className="text-sm text-muted-foreground">Auto-recalibration des modèles basée sur les erreurs passées</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-2xs text-muted-foreground font-mono">LEARNING CYCLES</div>
            <div className="text-lg font-bold text-primary font-mono">{cycleCount.toLocaleString()}</div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="text-right">
            <div className="text-2xs text-muted-foreground font-mono">GLOBAL SCORE</div>
            <div className="text-lg font-bold text-green-400 font-mono">{liveScore}</div>
          </div>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Learning Score', value: `${liveScore}`, unit: '/100', color: 'text-green-400', icon: 'AcademicCapIcon', desc: 'Score global apprentissage' },
          { label: 'Adaptation Velocity', value: '2.7', unit: 'pts/h', color: 'text-blue-400', icon: 'BoltIcon', desc: 'Vitesse recalibration' },
          { label: 'Confidence Decay', value: '0.14', unit: 'σ/24h', color: 'text-yellow-400', icon: 'ArrowTrendingDownIcon', desc: 'Dégradation confiance' },
          { label: 'Stability Index', value: '89.6', unit: '/100', color: 'text-purple-400', icon: 'ShieldCheckIcon', desc: 'Stabilité prédictions' },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-card border border-border rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xs text-muted-foreground font-mono uppercase tracking-wider">{kpi.label}</span>
              <Icon name={kpi.icon as Parameters<typeof Icon>[0]['name']} size={14} className={kpi.color} />
            </div>
            <div className="flex items-baseline gap-1">
              <span className={`text-2xl font-bold font-mono ${kpi.color}`}>{kpi.value}</span>
              <span className="text-xs text-muted-foreground">{kpi.unit}</span>
            </div>
            <p className="text-2xs text-muted-foreground mt-1">{kpi.desc}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {(['overview', 'errors', 'history'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${activeTab === tab ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {tab === 'overview' ? 'Vue Globale' : tab === 'errors' ? 'Analyse Erreurs' : 'Historique Ajustements'}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="grid grid-cols-3 gap-4">
          {/* Model selector */}
          <div className="bg-card border border-border rounded-lg p-4 space-y-2">
            <h3 className="text-sm font-semibold text-foreground mb-3">Modèles Actifs</h3>
            {MODELS.map(model => (
              <button
                key={model.name}
                onClick={() => setSelectedModel(model)}
                className={`w-full text-left p-2.5 rounded-lg border transition-all ${selectedModel.name === model.name ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">{model.name}</span>
                  <span className={`text-xs font-mono ${model.accuracy >= 70 ? 'text-green-400' : 'text-yellow-400'}`}>{model.accuracy}%</span>
                </div>
                <div className="mt-1 h-1 bg-border rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${model.learningScore}%` }} />
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-2xs text-muted-foreground">Score: {model.learningScore}</span>
                  <span className={`text-2xs ${model.drift > 1.5 ? 'text-orange-400' : 'text-green-400'}`}>Drift: {model.drift}σ</span>
                </div>
              </button>
            ))}
          </div>

          {/* Radar + metrics */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">{selectedModel.name} — Profil</h3>
            <ResponsiveContainer width="100%" height={200}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#334155" />
                <PolarAngleAxis dataKey="metric" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Radar dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div className="p-2 bg-background rounded border border-border">
                <div className="text-2xs text-muted-foreground">Adapt. Velocity</div>
                <div className="text-sm font-bold text-blue-400">{selectedModel.adaptationVelocity} pts/h</div>
              </div>
              <div className="p-2 bg-background rounded border border-border">
                <div className="text-2xs text-muted-foreground">Conf. Decay</div>
                <div className="text-sm font-bold text-yellow-400">{selectedModel.confidenceDecay} σ/24h</div>
              </div>
            </div>
          </div>

          {/* Learning curve */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Courbe d&apos;Apprentissage (30j)</h3>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={LEARNING_HISTORY}>
                <defs>
                  <linearGradient id="lgAcc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 9 }} interval={4} />
                <YAxis tick={{ fill: '#64748b', fontSize: 9 }} domain={[60, 85]} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, fontSize: 11 }} />
                <Area type="monotone" dataKey="accuracy" stroke="#3b82f6" fill="url(#lgAcc)" strokeWidth={2} name="Précision %" />
                <Line type="monotone" dataKey="learningScore" stroke="#a855f7" strokeWidth={1.5} dot={false} name="Learning Score" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {activeTab === 'errors' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Distribution des Erreurs (14j)</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={ERROR_ANALYSIS}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 9 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 9 }} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, fontSize: 11 }} />
                <Bar dataKey="overPrediction" fill="#ef4444" name="Sur-prédiction" radius={[2, 2, 0, 0]} />
                <Bar dataKey="underPrediction" fill="#f59e0b" name="Sous-prédiction" radius={[2, 2, 0, 0]} />
                <Bar dataKey="calibrationError" fill="#3b82f6" name="Erreur calibration" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Dérive Modèles (Drift Detection)</h3>
            <div className="space-y-3">
              {MODELS.map(m => (
                <div key={m.name} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-28 truncate">{m.name}</span>
                  <div className="flex-1 h-2 bg-border rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${m.drift > 1.5 ? 'bg-orange-400' : m.drift > 1 ? 'bg-yellow-400' : 'bg-green-400'}`}
                      style={{ width: `${Math.min(m.drift * 30, 100)}%` }}
                    />
                  </div>
                  <span className={`text-xs font-mono w-12 text-right ${m.drift > 1.5 ? 'text-orange-400' : m.drift > 1 ? 'text-yellow-400' : 'text-green-400'}`}>{m.drift}σ</span>
                  <span className={`text-2xs px-1.5 py-0.5 rounded border ${m.drift > 1.5 ? 'bg-orange-400/10 border-orange-400/30 text-orange-400' : 'bg-green-400/10 border-green-400/30 text-green-400'}`}>
                    {m.drift > 1.5 ? 'DRIFT' : 'STABLE'}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-background rounded border border-border">
              <div className="text-2xs text-muted-foreground mb-2 font-mono">PREDICTION STABILITY INDEX</div>
              <div className="grid grid-cols-3 gap-2">
                {MODELS.slice(0, 3).map(m => (
                  <div key={m.name} className="text-center">
                    <div className="text-lg font-bold text-purple-400 font-mono">{m.stabilityIndex}</div>
                    <div className="text-2xs text-muted-foreground truncate">{m.name}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Historique Complet des Ajustements</h3>
            <span className="text-2xs text-muted-foreground font-mono">{ADJUSTMENTS.length} ajustements récents</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  {['Timestamp', 'Modèle', 'Déclencheur', 'Type', 'Δ Précision', 'Avant', 'Après'].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-2xs font-medium text-muted-foreground uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ADJUSTMENTS.map((adj) => (
                  <tr key={adj.id} className="border-b border-border/50 hover:bg-background/30 transition-colors">
                    <td className="px-4 py-2.5 font-mono text-muted-foreground">{adj.timestamp}</td>
                    <td className="px-4 py-2.5 font-medium text-foreground">{adj.model}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{adj.trigger}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded border text-2xs font-mono ${TYPE_BG[adj.type]} ${TYPE_COLORS[adj.type]}`}>
                        {adj.type.toUpperCase()}
                      </span>
                    </td>
                    <td className={`px-4 py-2.5 font-mono font-bold ${adj.delta > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {adj.delta > 0 ? '+' : ''}{adj.delta}%
                    </td>
                    <td className="px-4 py-2.5 font-mono text-muted-foreground">{adj.accuracy_before}%</td>
                    <td className="px-4 py-2.5 font-mono text-green-400">{adj.accuracy_after}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
